'use client';

import {
  Badge,
  Button,
  Checkbox,
  Chip,
  Drawer,
  Fab,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
  type SxProps,
} from '@mui/material';
import {
  deleteClientCookie,
  FAILURES,
  FORCE_MOCK_COOKIE,
  getClientCookie,
  LATENCIES,
  MOCK_CONFIG_COOKIE,
  serializeMockConfig,
  setClientCookie,
  type BaseMockConfig,
} from '@repo/utils';
import { lockDevTools } from '@repo/utils/devTools/actions';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { COLORS } from '../../constants';
import { MaterialIcon } from '../materialIcon';

export const DevOptionSelect = <T extends string | number>({
  label,
  value,
  options,
  onChange,
  helper,
  disabled,
}: {
  label: string;
  value: T;
  options: readonly { id: T; label: string }[];
  onChange: (value: T) => void;
  helper?: string;
  disabled?: boolean;
}) => (
  <Stack sx={{ gap: 0.5 }}>
    <FormControl size="small" fullWidth disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value}
        onChange={(event) => onChange(options.find(({ id }) => `${id}` === `${event.target.value}`)!.id)}
      >
        {options.map(({ id, label: optionLabel }) => (
          <MenuItem key={id} value={id}>
            {optionLabel}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    {helper && (
      <Typography variant="body3" sx={{ color: COLORS.text.light }}>
        {helper}
      </Typography>
    )}
  </Stack>
);

/** Label, optional hint and a switch named by the label. */
export const DevToggleRow = ({
  id,
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Stack sx={{ gap: 0.5 }}>
    {/* Plain row: the theme draws FormControlLabel as a bordered input */}
    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <Typography id={id} variant="body1" sx={{ color: COLORS.text.main, fontWeight: 600 }}>
        {label}
      </Typography>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(_, value) => onChange(value)}
        slotProps={{ input: { 'aria-labelledby': id } }}
      />
    </Stack>
    <Typography variant="body3" sx={{ color: COLORS.text.light }}>
      {hint}
    </Typography>
  </Stack>
);

export const DevSectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" sx={{ color: COLORS.text.main, fontWeight: 700 }}>
    {children}
  </Typography>
);

export type DevCase<Id extends string> = { id: Id; group: string; label: string; description: string };
export type DevPreset<Id extends string> = { id: string; label: string; cases: readonly Id[] };

/**
 * Quick presets plus a grouped checklist of the cases a mock list holds. The picked ids keep the list order, so the
 * mock rows show in the same order however they were picked.
 */
export const DevCasePicker = <Id extends string, P extends DevPreset<Id>>({
  title,
  cases,
  presets,
  selected,
  onChange,
  onPreset = (preset) => onChange([...preset.cases]),
  isPresetActive = () => true,
  disabled,
}: {
  title: string;
  cases: readonly DevCase<Id>[];
  presets?: readonly P[];
  selected: readonly Id[];
  onChange: (ids: Id[]) => void;
  onPreset?: (preset: P) => void;
  // Extra condition for a preset to show as picked, beyond holding the same cases
  isPresetActive?: (preset: P) => boolean;
  disabled?: boolean;
}) => {
  const groups = [...new Set(cases.map(({ group }) => group))];
  const toggle = (caseId: Id) =>
    onChange(cases.map(({ id }) => id).filter((id) => (id === caseId) !== selected.includes(id)));
  const active = (preset: P) =>
    isPresetActive(preset) &&
    preset.cases.length === selected.length &&
    preset.cases.every((id) => selected.includes(id));

  return (
    <>
      {!!presets?.length && (
        <Stack sx={{ gap: 1 }}>
          <DevSectionTitle>Escenarios rápidos</DevSectionTitle>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
            {presets.map((preset) => (
              <Chip
                key={preset.id}
                label={preset.label}
                color={active(preset) ? 'primary' : 'default'}
                variant={active(preset) ? 'filled' : 'outlined'}
                disabled={disabled}
                onClick={() => onPreset(preset)}
              />
            ))}
          </Stack>
        </Stack>
      )}

      <Stack sx={{ gap: 1.5 }}>
        <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
          <DevSectionTitle>{title}</DevSectionTitle>
          <Typography variant="body3" sx={{ color: COLORS.text.light }}>
            {selected.length} de {cases.length}
          </Typography>
        </Stack>
        {groups.map((group) => (
          <Stack key={group} sx={{ gap: 0.25 }}>
            <Typography variant="body3" sx={{ color: COLORS.text.light, fontWeight: 600, textTransform: 'uppercase' }}>
              {group}
            </Typography>
            {cases
              .filter((item) => item.group === group)
              .map(({ id, label, description }) => (
                <Stack key={id} direction="row" sx={{ alignItems: 'flex-start', gap: 1 }}>
                  <Checkbox
                    size="small"
                    checked={selected.includes(id)}
                    disabled={disabled}
                    onChange={() => toggle(id)}
                    slotProps={{ input: { id: `dev-tools-case-${id}` } }}
                    sx={{ p: 0.5 }}
                  />
                  {/* The whole text toggles the case too */}
                  <Stack
                    component="label"
                    htmlFor={`dev-tools-case-${id}`}
                    sx={{ py: 0.5, minWidth: 0, cursor: disabled ? 'default' : 'pointer' }}
                  >
                    <Typography variant="body2" sx={{ color: COLORS.text.main, fontWeight: 600 }}>
                      {label}
                    </Typography>
                    <Typography variant="body3" sx={{ color: COLORS.text.light }}>
                      {description}
                    </Typography>
                  </Stack>
                </Stack>
              ))}
          </Stack>
        ))}
      </Stack>
    </>
  );
};

export type DevScenarioProps<C extends BaseMockConfig> = {
  config: C;
  update: (patch: Partial<C>) => void;
  disabled: boolean;
};

export type DevPanelProps<C extends BaseMockConfig> = {
  // The app's NEXT_PUBLIC_*_API_FORCEMOCK value and name: when set, mock is always on
  envForcesMock: boolean;
  envFlagName: string;
  defaults: C;
  parse: (value?: string | null) => C;
  // App scenario controls, inside the section that waits for the mock switch
  renderScenarios?: (props: DevScenarioProps<C>) => React.ReactNode;
  failureHelper?: string;
  // Controls that work without mock, below the mock section
  extras?: React.ReactNode;
  // Hooks for app-owned cookies: re-read on open, written on apply or mock login, cleared on reset
  onOpen?: () => void;
  onSave?: () => void;
  onReset?: () => void;
  // Shown while the token cookie is missing; signs in with the mock login and navigates
  mockLogin?: { tokenCookie: string; login: () => Promise<void> };
  fabSx?: SxProps;
};

/**
 * Dev-only panel to browse an app with mock data: turns the mock API on, sets latency and forced failures, plus the
 * app's own scenarios. Settings live in cookies, so they survive reloads; the @repo/*-api wrappers forward them to the
 * mock routes.
 */
export const DevPanel = <C extends BaseMockConfig>({
  envForcesMock,
  envFlagName,
  defaults,
  parse,
  renderScenarios,
  failureHelper,
  extras,
  onOpen,
  onSave,
  onReset,
  mockLogin,
  fabSx = { left: 16, bottom: 16 },
}: DevPanelProps<C>) => {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [mockOn, setMockOn] = useState(false);
  const [config, setConfig] = useState<C>(defaults);
  const [loggedIn, setLoggedIn] = useState(false);
  // Dot on the button while mock data is on
  const [badge, setBadge] = useState(false);

  // Cookies read on open, not on render: the server render has none, and another tab may have changed them
  useEffect(() => {
    if (!open) return;
    setMockOn(envForcesMock || getClientCookie(FORCE_MOCK_COOKIE) === 'true');
    setConfig(parse(getClientCookie(MOCK_CONFIG_COOKIE)));
    if (mockLogin) setLoggedIn(!!getClientCookie(mockLogin.tokenCookie));
    onOpen?.();
  }, [open]);
  useEffect(() => {
    setBadge(envForcesMock || getClientCookie(FORCE_MOCK_COOKIE) === 'true');
  }, []);

  const update = (patch: Partial<C>) => setConfig((current) => ({ ...current, ...patch }));

  const save = (mock: boolean) => {
    if (mock) setClientCookie(FORCE_MOCK_COOKIE, 'true');
    else deleteClientCookie(FORCE_MOCK_COOKIE);
    setClientCookie(MOCK_CONFIG_COOKIE, serializeMockConfig(config));
    onSave?.();
  };

  const apply = () => {
    save(mockOn);
    setBadge(mockOn);
    setOpen(false);
    router.refresh();
  };

  const enterAsMockUser = async () => {
    save(true);
    setBadge(true);
    setOpen(false);
    await mockLogin?.login();
  };

  const reset = () => {
    deleteClientCookie([FORCE_MOCK_COOKIE, MOCK_CONFIG_COOKIE]);
    onReset?.();
    setBadge(envForcesMock);
    setOpen(false);
    router.refresh();
  };

  // Drops the unlock and the mock settings; the layout then stops rendering this panel
  const lock = async () => {
    onReset?.();
    setOpen(false);
    await lockDevTools();
    router.refresh();
  };

  return (
    <>
      <Fab
        size="small"
        aria-label="Herramientas de desarrollo"
        data-testid="dev-tools"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          zIndex: 'speedDial',
          bgcolor: COLORS.text.main,
          color: '#fff',
          '&:hover': { bgcolor: COLORS.text.main },
          ...fabSx,
        }}
      >
        <Badge color="success" variant="dot" invisible={!badge} overlap="circular">
          <MaterialIcon icon="construction" size={20} />
        </Badge>
      </Fab>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{ paper: { sx: { width: { xs: '100%', sm: 380 } } } }}
      >
        <Stack sx={{ p: 3, gap: 2.5 }}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h1">Herramientas de desarrollo</Typography>
            <Button onClick={() => setOpen(false)} aria-label="Cerrar" sx={{ minWidth: 0 }}>
              <MaterialIcon icon="close" />
            </Button>
          </Stack>

          <DevToggleRow
            id="dev-tools-mock"
            label="Usar datos mock"
            hint={
              envForcesMock
                ? `${envFlagName}=true: el mock está siempre activo.`
                : 'Todas las llamadas a la API van a las rutas /mock. El usuario real no se usa.'
            }
            checked={mockOn}
            disabled={envForcesMock}
            onChange={setMockOn}
          />

          {/* Only the mock routes read these, so they wait for the switch above */}
          <Stack aria-disabled={!mockOn} sx={{ gap: 2.5, opacity: mockOn ? 1 : 0.45, transition: 'opacity 150ms' }}>
            {!mockOn && (
              <Typography variant="body3" sx={{ color: COLORS.text.light }}>
                Activá &quot;Usar datos mock&quot; para elegir escenarios.
              </Typography>
            )}
            {renderScenarios?.({ config, update, disabled: !mockOn })}
            <DevOptionSelect
              label="Demora de la API"
              value={config.latency}
              options={LATENCIES}
              disabled={!mockOn}
              onChange={(latency) => update({ latency } as Partial<C>)}
            />
            <DevOptionSelect
              label="Forzar error"
              value={config.failure}
              options={FAILURES}
              disabled={!mockOn}
              onChange={(failure) => update({ failure } as Partial<C>)}
              helper={failureHelper}
            />
          </Stack>

          {extras}

          {/* Pinned to the bottom of the panel, so applying never needs scrolling back down */}
          <Stack
            sx={{
              position: 'sticky',
              bottom: 0,
              // Outlined input labels sit at z-index 1 and would show through while scrolling
              zIndex: 2,
              gap: 1,
              mx: -3,
              mb: -3,
              p: 3,
              pt: 2,
              bgcolor: COLORS.surface.raised,
              borderTop: `1px solid ${COLORS.border.default}`,
            }}
          >
            <Button variant="contained" onClick={apply}>
              Aplicar
            </Button>
            {mockLogin && !loggedIn && (
              <Button variant="outlined" onClick={enterAsMockUser}>
                Entrar como usuario mock
              </Button>
            )}
            <Button onClick={reset}>Restablecer todo</Button>
            {/* Local dev is always unlocked, so there is nothing to lock */}
            {process.env.NODE_ENV !== 'development' && (
              <Button color="error" onClick={lock}>
                Bloquear herramientas
              </Button>
            )}
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
};
